// File: frontend/src/components/admin/AdminMatches.tsx
// src/components/admin/AdminMatches.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaPlus,
  FaEdit,
  FaTrash,
  FaFutbol,
  FaBasketballBall,
  FaVolleyballBall,
  FaFootballBall,
  FaBaseballBall,
  FaHockeyPuck,
  FaMale,
  FaFemale,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExchangeAlt,
  FaHashtag,
  FaUserTie,
  FaLock,
  FaUnlock,
  FaInfoCircle,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";
import { BsPeopleFill } from "react-icons/bs";

// Number input with custom, always-visible up/down arrows (native spinner is unreliable)
import NumberStepper from "./NumberStepper";

import {
  AdminMatchDto,
  AdminMatchFilters,
  AdminMatchPageResponse,
  MatchRefereeInfo,
  MatchRefereeRole,
  SPORT_REFEREE_ROLES,
  SPORTS_WITH_EXTRA_TIME,
  SPORTS_WITH_PENALTY,
  SPORTS_WITH_MULTI_OT,
  getDefaultRefereeRole,
} from "../../types/adminMatch.types";
import { MatchStatusEnum } from "../../models/enums/MatchStatusEnum";
import { SportTypeEnum } from "../../models/enums/SportTypeEnum";
import { MatchGenderEnum } from "../../models/enums/MatchGenderEnum";
import { TeamModel } from "../../models/TeamModel";
import { ClubModel } from "../../models/ClubModel";
import { PersonModel } from "../../models/PersonModel";
import { teamService } from "../../services/teamService";
import {
  getAdminMatches,
  createAdminMatch,
  updateAdminMatch,
  updateMatchScore,
  updateMatchStatus,
  deleteAdminMatch,
  addMatchReferee,
  removeMatchReferee,
  setMatchLocked,
  toggleMatchActive,
} from "../../services/adminMatchService";
import { getAdminPeople } from "../../services/adminPersonService";
import { stadiumService } from "../../services/stadiumService";
import { StadiumModel } from "../../models/StadiumModel";
import { Pagination } from "../../utils/Pagination";
// Diacritic-blind text folding for the club/league/round pickers below.
// Turkish İ/ı and every other Latin diacritic are folded to plain ASCII so a
// German or US keyboard finds the same rows a Turkish keyboard does.
import { normalizeSearchText } from "../../utils/searchUtils";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner } from "../common/AdminBanner";
import { ConfirmModal } from "../common/ConfirmModal";
import { LockReasonFields } from "./common/LockReasonFields";
import { LockInfoModal } from "./common/LockInfoModal";
import {
  DROPDOWN_BLUR_DELAY_MS,
  ADMIN_CLUB_SHORT_NAME_MAX_LENGTH,
  ADMIN_PERSON_NAME_MAX_LENGTH,
  ADMIN_MATCH_LEAGUE_MAX_LENGTH,
  ADMIN_MATCH_ROUND_MAX_LENGTH,
  ADMIN_MATCH_STADIUM_NAME_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter } from "./common";
import {
  formatMatchDateLocal,
  formatMatchTimeLocal,
  localInputToUtcParts,
  utcPartsToLocalInput,
} from "../../utils/dateUtils";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import { InputClearButton } from "../common/InputClearButton";
import "./AdminMatches.css";
import { MdCountryDto, MdLeagueDto, MdRoundDto, getPublicCountries, getPublicLeagues, getPublicRounds } from "../../services/masterDataService";
import { getCountryLabel } from "../../utils/countryUtils";

interface AdminMatchesProps {
  isAdmin: boolean;
}

/**
 * Returns the referee roles applicable for a given sport type.
 * Delegates to the SPORT_REFEREE_ROLES mapping defined in adminMatch.types.ts.
 * Falls back to ["OTHER"] for unknown sport types.
 */
const getRefereeRolesForSport = (sportType: string): MatchRefereeRole[] => {
  return SPORT_REFEREE_ROLES[sportType as SportTypeEnum] ?? ["OTHER"];
};

// Synthetic country code the backend uses for matches between clubs of
// different countries — kept in sync with MatchSpecifications.INTERNATIONAL_COUNTRY.
const INTERNATIONAL_COUNTRY = "INTL";

// Status badge color mapping
const getStatusColor = (status: MatchStatusEnum): string => {
  const colors: Record<MatchStatusEnum, string> = {
    [MatchStatusEnum.SCHEDULED]: "match-status-scheduled",
    [MatchStatusEnum.LIVE]: "match-status-live",
    [MatchStatusEnum.COMPLETED]: "match-status-completed",
    [MatchStatusEnum.CANCELLED]: "match-status-cancelled",
    [MatchStatusEnum.POSTPONED]: "match-status-postponed",
    [MatchStatusEnum.ABANDONED]: "match-status-abandoned",
  };
  return colors[status] || "match-status-default";
};

// Returns the current football season string (e.g. "2026-2027").
// European football seasons roll over in July: July–December maps to
// "thisYear-nextYear", January–June maps to "lastYear-thisYear".
const getCurrentSeason = (): string => {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${startYear + 1}`;
};

export const AdminMatches: React.FC<AdminMatchesProps> = ({ isAdmin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Data state
  const [matches, setMatches] = useState<AdminMatchDto[]>([]);

  // ── Lock modal state (shown only when locking, not unlocking) ──
  const [lockTarget, setLockTarget] = useState<AdminMatchDto | null>(null);
  const [lockReasonDraft, setLockReasonDraft] = useState("");
  const [lockReasonVisibleDraft, setLockReasonVisibleDraft] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockInfoTarget, setLockInfoTarget] = useState<AdminMatchDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // Same-day duplicate warning state — shown when backend returns SAME_DAY_DUPLICATE
  const [sameDayWarning, setSameDayWarning] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Filter state
  const [filters, setFilters] = useState<AdminMatchFilters>({
    status: "",
    sportType: "",
    matchGender: "",
    country: "",
    active: "",
    locked: "",
    search: "",
    sortBy: "matchDate",
    sortDir: "desc",
  });
  const [searchInput, setSearchInput] = useState("");

  // Master-data countries — feeds the country filter dropdown
  const [filterCountries, setFilterCountries] = useState<MdCountryDto[]>([]);
  const [showFilters, setShowFilters] = useState(true);

  // Load the country list once; failures leave the dropdown with only
  // "all countries" + "international" so the panel stays usable.
  useEffect(() => {
    getPublicCountries()
      .then(setFilterCountries)
      .catch(() => setFilterCountries([]));
  }, []);

  // Modal state
  const [modalType, setModalType] = useState<
    "create" | "edit" | "score" | "status" | "delete" | "referees" | null
  >(null);
  const [selectedMatch, setSelectedMatch] = useState<AdminMatchDto | null>(null);

  // Referee modal state
  const [refereeForm, setRefereeForm] = useState<{ refereeRole: MatchRefereeRole }>({ refereeRole: "MAIN" });
  const [refereeQuery, setRefereeQuery] = useState("");
  const [refereeResults, setRefereeResults] = useState<PersonModel[]>([]);
  const [refereeSearchLoading, setRefereeSearchLoading] = useState(false);
  const [selectedReferee, setSelectedReferee] = useState<PersonModel | null>(null);
  const [showRefereeDrop, setShowRefereeDrop] = useState(false);
  const [refereeSaving, setRefereeSaving] = useState(false);
  const [refereeError, setRefereeError] = useState<string | null>(null);
  const refereeDropRef = useRef<HTMLDivElement>(null);

  // Outside-click detection for the team-branch dropdowns (home/away).
  // The refs sit on the club-search-input-wrap elements so clicks inside the
  // input or the attached dropdown keep the list open.
  const homeBranchRef = useRef<HTMLDivElement>(null);
  const awayBranchRef = useRef<HTMLDivElement>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    homeTeamId: 0,
    awayTeamId: 0,
    matchDate: "",
    matchTime: "",
    stadiumName: "",
    sportType: "FOOTBALL" as SportTypeEnum,
    matchGender: "MALE" as MatchGenderEnum,
    league: "",
    season: getCurrentSeason(),
    round: "",
    status: "SCHEDULED" as MatchStatusEnum,
    homeScore: 0,
    awayScore: 0,
    // Extra time and penalty scores — null means not played
    homeScoreEt: null as number | null,
    awayScoreEt: null as number | null,
    homeScorePen: null as number | null,
    awayScorePen: null as number | null,
    overtimePeriods: 0,
    // Public visibility — editable only in the edit modal
    active: true,
  });

  // Team search state - 2-step: club search → team selection
  const [teams, setTeams] = useState<TeamModel[]>([]);

  // Home side club search
  const [homeClubSearch, setHomeClubSearch] = useState("");
  const [selectedHomeClub, setSelectedHomeClub] = useState<ClubModel | null>(null);
  const [showHomeClubDropdown, setShowHomeClubDropdown] = useState(false);
  const [homeTeamOptions, setHomeTeamOptions] = useState<TeamModel[]>([]);

  // Away side club search
  const [awayClubSearch, setAwayClubSearch] = useState("");
  const [selectedAwayClub, setSelectedAwayClub] = useState<ClubModel | null>(null);
  const [showAwayClubDropdown, setShowAwayClubDropdown] = useState(false);
  const [awayTeamOptions, setAwayTeamOptions] = useState<TeamModel[]>([]);

  // Team-branch dropdown visibility. The branch list now renders attached
  // directly under the club input (same anchor as the club search results),
  // replacing the old detached trigger-button + popup combo.
  const [showHomeTeamBranchDropdown, setShowHomeTeamBranchDropdown] = useState(false);
  const [showAwayTeamBranchDropdown, setShowAwayTeamBranchDropdown] = useState(false);

  // Score/status form state
  const [scoreForm, setScoreForm] = useState({
    homeScore: 0,
    awayScore: 0,
    homeScoreEt: null as number | null,
    awayScoreEt: null as number | null,
    homeScorePen: null as number | null,
    awayScorePen: null as number | null,
    overtimePeriods: 0,
  });
  const [statusForm, setStatusForm] = useState<MatchStatusEnum>(MatchStatusEnum.SCHEDULED);

  // Stadium search state for the create/edit form
  const [stadiumSearch, setStadiumSearch] = useState("");
  const [stadiumOptions, setStadiumOptions] = useState<StadiumModel[]>([]);
  const [showStadiumDropdown, setShowStadiumDropdown] = useState(false);
  // True once the current value was picked from the suggestion list (or pre-filled
  // from an existing match) — drives the same "confirmed" input highlight the
  // home/away club fields get via .club-selected, instead of free-typed text
  // looking identical to a matched stadium
  const [stadiumConfirmed, setStadiumConfirmed] = useState(false);

  // League search state
  const [leagueSearch, setLeagueSearch] = useState("");
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false);
  const [roundSearch, setRoundSearch] = useState("");
  const [showRoundDropdown, setShowRoundDropdown] = useState(false);

  // Master data: leagues + rounds from API, replaces hardcoded constants
  const [mdLeagues, setMdLeagues] = useState<MdLeagueDto[]>([]);
  const [mdRounds, setMdRounds] = useState<MdRoundDto[]>([]);

  // Reload leagues from API whenever sport type changes
  useEffect(() => {
    getPublicLeagues(formData.sportType)
      .then(setMdLeagues)
      .catch(() => setMdLeagues([]));
  }, [formData.sportType]);

  // Load rounds once on mount (universal + sport-specific)
  useEffect(() => {
    getPublicRounds(formData.sportType)
      .then(setMdRounds)
      .catch(() => setMdRounds([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load teams on mount
  useEffect(() => {
    const loadTeams = async () => {
      try {
        const response = await teamService.getAllTeams(0, 500);
        setTeams(response?.content || []);
      } catch (err) {
        console.error("Failed to load teams:", err);
        setTeams([]);
      }
    };
    loadTeams();
  }, []);

  // Derive unique clubs from the already-loaded teams list (no extra API call needed)
  const allClubs: ClubModel[] = React.useMemo(() => {
    const seen = new Set<string>();   // use string keys to avoid number/string mismatch
    const clubs: ClubModel[] = [];
    teams.forEach((t) => {
      const key = String(t.clubId);
      if (!seen.has(key)) {
        seen.add(key);
        clubs.push({
          id: Number(t.clubId),
          shortName: t.clubShortName,
          nameTr: null,
          nameEn: null,
          longName: t.clubLongName,
          abbreviation: t.clubAbbreviation,
          slug: t.clubSlug,
          logoUrl: t.clubLogoUrl,
          color1: null,
          color2: null,
          color3: null,
          country: t.clubCountry,
          city: t.clubCity,
          foundedYear: null,
          active: true,
          teams: [],
        });
      }
    });
    return clubs.sort((a, b) => a.shortName.localeCompare(b.shortName));
  }, [teams]);

  // Count how many distinct sports each club has — used for smart display name fallback
  const clubSportCount = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    teams.forEach((t) => {
      const key = String(t.clubId);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key)!.add(t.sportType);
    });
    return map;
  }, [teams]);

  // Returns the best display name for a team:
  // 1. Use displayName if explicitly set in DB (e.g. "Fenerbahçe Beko")
  // 2. If the club has teams in multiple sports, append sport label (e.g. "Fenerbahçe Basketbol")
  // 3. Otherwise just use clubShortName
  // 4. Always append the sub-team code when set (e.g. "U19", "1B") so branches that share
  //    the same club/sport/gender combination stay distinguishable in dropdowns instead of
  //    rendering as identical-looking duplicates
  const getTeamDisplayName = React.useCallback(
    (team: TeamModel): string => {
      let base: string;
      if (team.displayName) {
        base = team.displayName;
      } else {
        const sports = clubSportCount.get(String(team.clubId));
        if (sports && sports.size > 1) {
          // Append localized sport label so admin can tell branches apart
          const label = t(`sport_type_${team.sportType.toLowerCase()}`);
          base = `${team.clubShortName} ${label}`;
        } else {
          base = team.clubShortName;
        }
      }
      const codeSuffix = team.teamCode ? ` ${team.teamCode.toUpperCase()}` : "";
      return `${base}${codeSuffix}`;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clubSportCount]
  );

  // Compact sport/gender icons for the team-branch dropdown — same icon set already
  // used for match rows elsewhere in the app. Replaces the raw "FOOTBALL / MALE" text,
  // which was both untranslated and wide enough to wrap onto two lines.
  const getBranchSportIcon = (sportType: string) => {
    const p = { size: 12, style: { opacity: 0.6, flexShrink: 0 } };
    switch (sportType) {
      case "FOOTBALL":          return <FaFutbol {...p} />;
      case "BASKETBALL":        return <FaBasketballBall {...p} />;
      case "VOLLEYBALL":        return <FaVolleyballBall {...p} />;
      case "AMERICAN_FOOTBALL": return <FaFootballBall {...p} />;
      case "BASEBALL":          return <FaBaseballBall {...p} />;
      case "ICE_HOCKEY":        return <FaHockeyPuck {...p} />;
      case "HANDBALL":          return <MdSportsHandball {...p} />;
      default:                  return <FaFutbol {...p} />;
    }
  };

  const getBranchSportLabel = (sportType: string) => t(`sport_type_${sportType.toLowerCase()}`, sportType);

  const getBranchGenderIcon = (gender: string) => {
    const p = { size: 11, style: { opacity: 0.6, flexShrink: 0 } };
    switch (gender) {
      case "FEMALE": return <FaFemale {...p} />;
      case "MIXED":  return <BsPeopleFill {...p} />;
      default:       return <FaMale {...p} />;
    }
  };

  const getBranchGenderLabel = (gender: string) => {
    switch (gender) {
      case "FEMALE": return t("match_gender_female");
      case "MIXED":  return t("match_gender_mixed");
      default:       return t("match_gender_male");
    }
  };

  // Short label used inside the branch dropdown and the branch pill. The club
  // is already visible in the input above, so repeating "Fenerbahçe ..." on
  // every row is redundant and breaks alignment — strip the club prefix and
  // show only the distinguishing part (e.g. "Futbol U17", "U19"). The full
  // name stays available via the title tooltip. Falls back to the sport label
  // when stripping leaves nothing (club with a single unlabeled team).
  const getBranchShortLabel = (team: TeamModel): string => {
    const full = getTeamDisplayName(team);
    const prefix = team.clubShortName;
    let label = full;
    if (prefix && full.toLowerCase().startsWith(prefix.toLowerCase())) {
      label = full.slice(prefix.length).trim();
    }
    return label || getBranchSportLabel(team.sportType);
  };

  // Filter clubs matching home search input
  const filteredHomeClubs = React.useMemo(() => {
    if (!homeClubSearch || selectedHomeClub) return [];
    // normalizeSearchText on BOTH sides: typing "ispanya" with a plain i must
    // match "İspanya", and "besiktas" must match "Beşiktaş"
    const q = normalizeSearchText(homeClubSearch);
    return allClubs
      .filter(
        (c) =>
          normalizeSearchText(c.shortName).includes(q) ||
          (c.longName && normalizeSearchText(c.longName).includes(q)) ||
          (c.abbreviation && normalizeSearchText(c.abbreviation).includes(q))
      )
      .slice(0, 10);
  }, [homeClubSearch, allClubs, selectedHomeClub]);

  // Filter clubs matching away search input
  const filteredAwayClubs = React.useMemo(() => {
    if (!awayClubSearch || selectedAwayClub) return [];
    // normalizeSearchText on BOTH sides: typing "ispanya" with a plain i must
    // match "İspanya", and "besiktas" must match "Beşiktaş"
    const q = normalizeSearchText(awayClubSearch);
    return allClubs
      .filter(
        (c) =>
          normalizeSearchText(c.shortName).includes(q) ||
          (c.longName && normalizeSearchText(c.longName).includes(q)) ||
          (c.abbreviation && normalizeSearchText(c.abbreviation).includes(q))
      )
      .slice(0, 10);
  }, [awayClubSearch, allClubs, selectedAwayClub]);

  // Re-compute team options when sport type or gender changes (club already selected)
  useEffect(() => {
    if (!selectedHomeClub) return;
    const options = getTeamOptionsForClub(selectedHomeClub.id);
    setHomeTeamOptions(options);
    setFormData((p) => ({ ...p, homeTeamId: options.length === 1 ? Number(options[0].id) : 0 }));
  }, [formData.sportType, formData.matchGender]); // eslint-disable-line

  useEffect(() => {
    if (!selectedAwayClub) return;
    const options = getTeamOptionsForClub(selectedAwayClub.id);
    setAwayTeamOptions(options);
    setFormData((p) => ({ ...p, awayTeamId: options.length === 1 ? Number(options[0].id) : 0 }));
  }, [formData.sportType, formData.matchGender]); // eslint-disable-line


  // Referee person search — debounced, searches all active people
  useEffect(() => {
    if (refereeQuery.trim().length < 2) {
      setRefereeResults([]);
      setShowRefereeDrop(false);
      return;
    }
    const timer = setTimeout(async () => {
      setRefereeSearchLoading(true);
      try {
        const res = await getAdminPeople(0, 10, {
          search: refereeQuery.trim(),
          nationality: "",
          birthCountry: "",
          active: "true",
          clubId: "",
          currentClubId: "",
          sortBy: "lastName",
          sortDir: "asc",
        });
        setRefereeResults(res.content);
        setShowRefereeDrop(res.content.length > 0);
      } catch {
        setRefereeResults([]);
      } finally {
        setRefereeSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [refereeQuery]);

  // Close referee dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (refereeDropRef.current && !refereeDropRef.current.contains(e.target as Node)) {
        setShowRefereeDrop(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close the attached branch dropdowns on outside click. Clicks inside the
  // club input / dropdown (contained by the refs) keep the list open; item
  // selection itself closes via selectHomeTeamBranch / selectAwayTeamBranch.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (homeBranchRef.current && !homeBranchRef.current.contains(e.target as Node)) {
        setShowHomeTeamBranchDropdown(false);
      }
      if (awayBranchRef.current && !awayBranchRef.current.contains(e.target as Node)) {
        setShowAwayTeamBranchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Open referee management modal — uses current match data (referees list is already in AdminMatchDto)
  const openRefereesModal = (match: AdminMatchDto) => {
    setSelectedMatch(match);
    setRefereeForm({ refereeRole: getDefaultRefereeRole(match.sportType) });
    setRefereeQuery("");
    setRefereeResults([]);
    setSelectedReferee(null);
    setShowRefereeDrop(false);
    setRefereeError(null);
    setModalType("referees");
  };

  // Add a referee assignment to the currently open match
  const handleAddReferee = async () => {
    if (!selectedMatch || !selectedReferee) {
      setRefereeError(t("admin_match_referee_person_required"));
      return;
    }
    setRefereeSaving(true);
    setRefereeError(null);
    try {
      const updated = await addMatchReferee(selectedMatch.id, {
        personId: selectedReferee.id,
        refereeRole: refereeForm.refereeRole,
      });
      // Update selectedMatch inline so the list refreshes without closing the modal
      setSelectedMatch(updated);
      // Also update the row in the matches table
      setMatches((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      // Reset add-form fields
      setRefereeQuery("");
      setSelectedReferee(null);
      setRefereeResults([]);
      setRefereeForm({ refereeRole: getDefaultRefereeRole(selectedMatch.sportType) });
    } catch (err: any) {
      setRefereeError(err.message || t("admin_match_referee_add_error"));
    } finally {
      setRefereeSaving(false);
    }
  };

  // Remove a referee assignment
  const handleRemoveReferee = async (assignment: MatchRefereeInfo) => {
    if (!selectedMatch) return;
    setRefereeError(null);
    try {
      const updated = await removeMatchReferee(selectedMatch.id, assignment.assignmentId);
      setSelectedMatch(updated);
      setMatches((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    } catch (err: any) {
      setRefereeError(err.message || t("admin_match_referee_remove_error"));
    }
  };

  // Fetch matches
  const fetchMatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response: AdminMatchPageResponse = await getAdminMatches(
        currentPage,
        20,
        filters
      );
      setMatches(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err: any) {
      setError(err.message || t("admin_match_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, t]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMessage]);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, search: searchInput }));
  };

  // Clear-X next to the search box: empties the field AND drops the applied search
  const handleClearSearch = () => {
    setSearchInput("");
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  // Handle sort
  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(0);
  };

  // Get sort icon
  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc" ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      status: "",
      sportType: "",
      matchGender: "",
      country: "",
      active: "",
      locked: "",
      search: "",
      sortBy: "matchDate",
      sortDir: "desc",
    });
    setSearchInput("");
    setCurrentPage(0);
  };

  // ── Quick date filters (yesterday / today / tomorrow) ──────────────────────
  // These only pin startDate and endDate to the same calendar day. The backend
  // already turns an equal start/end pair into a BETWEEN match_date filter, so
  // no API or DTO change is needed here.
  const getOffsetDateString = (offsetDays: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    // Build the local calendar date manually — toISOString() would convert to
    // UTC first and shift the day for users east/west of Greenwich.
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };

  const isQuickDateActive = (offsetDays: number): boolean => {
    const target = getOffsetDateString(offsetDays);
    return filters.startDate === target && filters.endDate === target;
  };

  // Clicking the already active button clears the range again (toggle behaviour)
  const applyQuickDate = (offsetDays: number) => {
    const target = getOffsetDateString(offsetDays);
    setFilters((prev) =>
      prev.startDate === target && prev.endDate === target
        ? { ...prev, startDate: undefined, endDate: undefined }
        : { ...prev, startDate: target, endDate: target }
    );
    setCurrentPage(0);
  };

  // Pagination handler (1-based from Pagination component)
  const handlePaginate = (pageNumber: number) => {
    setCurrentPage(pageNumber - 1);
  };

  // Open create modal
  // Navigate to match detail page
  const viewMatch = (match: AdminMatchDto) => {
    const matchUrl = generateMatchUrlFromEntry(
      match.homeTeam.clubShortName,
      match.awayTeam.clubShortName,
      new Date(match.matchDate),
      match.id
    );
    navigate(matchUrl);
  };

  const openCreateModal = () => {
    // Deliberately left blank instead of defaulting to today. A pre-filled
    // date is easy to miss and silently save as "today" for a match that
    // was actually scheduled for some other day — leaving it empty forces
    // the admin to consciously pick one. handleCreateMatch() already blocks
    // submission when matchDate is empty (see the required-fields check),
    // so this doesn't need any new validation.
    setFormData({
      homeTeamId: 0,
      awayTeamId: 0,
      matchDate: "",
      matchTime: "19:00",
      stadiumName: "",
      sportType: SportTypeEnum.FOOTBALL,
      matchGender: MatchGenderEnum.MALE,
      league: "",
      season: getCurrentSeason(),
      round: "",
      status: MatchStatusEnum.SCHEDULED,
      homeScore: 0,
      awayScore: 0,
      homeScoreEt: null,
      awayScoreEt: null,
      homeScorePen: null,
      awayScorePen: null,
      overtimePeriods: 0,
      // New matches are always publicly visible
      active: true,
    });
    // Reset 2-step club selection
    setHomeClubSearch(""); setSelectedHomeClub(null); setHomeTeamOptions([]);
    setAwayClubSearch(""); setSelectedAwayClub(null); setAwayTeamOptions([]);
    setShowHomeTeamBranchDropdown(false); setShowAwayTeamBranchDropdown(false);
    // Reset stadium search
    setStadiumSearch(""); setStadiumOptions([]); setStadiumConfirmed(false);
    // Reset league search
    setLeagueSearch("");
    setRoundSearch("");
    setModalType("create");
  };

  // Open edit modal
  const openEditModal = (match: AdminMatchDto) => {
    setSelectedMatch(match);
    setFormData({
      homeTeamId: Number(match.homeTeam.teamId),
      awayTeamId: Number(match.awayTeam.teamId),
      // Convert the stored UTC date+time back to the admin's local timezone for editing
      ...utcPartsToLocalInput(match.matchDate, match.matchTime),
      stadiumName: match.stadiumName || "",
      sportType: match.sportType,
      matchGender: match.matchGender,
      league: match.league || "",
      season: match.season || getCurrentSeason(),
      round: match.round || "",
      status: match.status,
      homeScore: match.homeScore,
      awayScore: match.awayScore,
      homeScoreEt: match.homeScoreEt ?? null,
      awayScoreEt: match.awayScoreEt ?? null,
      homeScorePen: match.homeScorePen ?? null,
      awayScorePen: match.awayScorePen ?? null,
      overtimePeriods: match.overtimePeriods ?? 0,
      active: match.active,
    });
    // Pre-fill club display for edit mode - build a minimal ClubModel from match data
    const homeClub: ClubModel = {
      id: match.homeTeam.clubId,
      shortName: match.homeTeam.clubShortName,
      nameTr: null,
      nameEn: null,
      longName: match.homeTeam.clubLongName,
      abbreviation: match.homeTeam.clubAbbreviation,
      slug: match.homeTeam.clubSlug || "",
      logoUrl: match.homeTeam.clubLogoUrl,
      color1: null,
      color2: null,
      color3: null,
      country: match.homeTeam.clubCountry,
      city: null,
      foundedYear: null,
      active: true,
      teams: [],
    };
    const awayClub: ClubModel = {
      id: match.awayTeam.clubId,
      shortName: match.awayTeam.clubShortName,
      nameTr: null,
      nameEn: null,
      longName: match.awayTeam.clubLongName,
      abbreviation: match.awayTeam.clubAbbreviation,
      slug: match.awayTeam.clubSlug || "",
      logoUrl: match.awayTeam.clubLogoUrl,
      color1: null,
      color2: null,
      color3: null,
      country: match.awayTeam.clubCountry,
      city: null,
      foundedYear: null,
      active: true,
      teams: [],
    };
    setHomeClubSearch(match.homeTeam.clubShortName);
    setSelectedHomeClub(homeClub);
    setAwayClubSearch(match.awayTeam.clubShortName);
    setSelectedAwayClub(awayClub);
    // Branch dropdowns start closed in edit mode — the team is already known
    setShowHomeTeamBranchDropdown(false);
    setShowAwayTeamBranchDropdown(false);
    // Pre-fill stadium search with existing value
    setStadiumSearch(match.stadiumName || ""); setStadiumOptions([]);
    setStadiumConfirmed(!!match.stadiumName);
    // Pre-fill league search with existing value
    setLeagueSearch(match.league || "");
    setRoundSearch(match.round || "");

    // Pre-populate team options from the loaded teams list.
    // This prevents the sport/gender useEffect from firing and resetting
    // homeTeamId/awayTeamId to 0 when the match team is not yet in the teams list
    // (e.g. a team added after this component mounted — stale teams array).
    const existingHomeTeam = teams.find((t) => t.id === match.homeTeam.teamId);
    const existingAwayTeam = teams.find((t) => t.id === match.awayTeam.teamId);

    // Build minimal TeamModel fallbacks from match data if teams list is stale
    const homeTeamFallback: TeamModel = {
      id: match.homeTeam.teamId, displayName: null,
      sportType: match.homeTeam.sportType as string,
      gender: match.homeTeam.gender as string, active: true,
      teamCode: null,
      clubId: match.homeTeam.clubId, clubShortName: match.homeTeam.clubShortName,
      clubLongName: match.homeTeam.clubLongName, clubAbbreviation: match.homeTeam.clubAbbreviation,
      clubSlug: match.homeTeam.clubSlug || "", clubLogoUrl: match.homeTeam.clubLogoUrl,
      clubCountry: match.homeTeam.clubCountry, clubCity: null,
      // Match payload carries no club brand colors — banner falls back to neutral gradient
      clubColor1: null, clubColor2: null, clubColor3: null,
    };
    const awayTeamFallback: TeamModel = {
      id: match.awayTeam.teamId, displayName: null,
      sportType: match.awayTeam.sportType as string,
      gender: match.awayTeam.gender as string, active: true,
      teamCode: null,
      clubId: match.awayTeam.clubId, clubShortName: match.awayTeam.clubShortName,
      clubLongName: match.awayTeam.clubLongName, clubAbbreviation: match.awayTeam.clubAbbreviation,
      clubSlug: match.awayTeam.clubSlug || "", clubLogoUrl: match.awayTeam.clubLogoUrl,
      clubCountry: match.awayTeam.clubCountry, clubCity: null,
      // Match payload carries no club brand colors — banner falls back to neutral gradient
      clubColor1: null, clubColor2: null, clubColor3: null,
    };

    // Guarantees options.length === 1 so the useEffect keeps homeTeamId/awayTeamId intact
    setHomeTeamOptions([existingHomeTeam ?? homeTeamFallback]);
    setAwayTeamOptions([existingAwayTeam ?? awayTeamFallback]);

    setModalType("edit");
  };

  // Open score modal
  const openScoreModal = (match: AdminMatchDto) => {
    setSelectedMatch(match);
    setScoreForm({
      homeScore: match.homeScore,
      awayScore: match.awayScore,
      homeScoreEt: match.homeScoreEt,
      awayScoreEt: match.awayScoreEt,
      homeScorePen: match.homeScorePen,
      awayScorePen: match.awayScorePen,
      overtimePeriods: match.overtimePeriods ?? 0,
    });
    setModalType("score");
  };

  // Open status modal
  const openStatusModal = (match: AdminMatchDto) => {
    setSelectedMatch(match);
    setStatusForm(match.status);
    setModalType("status");
  };

  // Open delete modal
  const openDeleteModal = (match: AdminMatchDto) => {
    setSelectedMatch(match);
    setModalType("delete");
  };

  // Close modal
  // ── Topic lock/unlock ──────────────────────────────────────────────────────
  // Quick-toggle public visibility directly from the table row
  const handleToggleActive = async (match: AdminMatchDto) => {
    const name = `${match.homeTeam.snapshotName} vs ${match.awayTeam.snapshotName}`;
    try {
      await toggleMatchActive(match.id, !match.active);
      setSuccessMessage(
        !match.active
          ? t("admin_toggle_activated", { name })
          : t("admin_toggle_deactivated", { name }),
      );
      fetchMatches();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  const handleToggleLock = async (match: AdminMatchDto) => {
    if (!match.isLocked) {
      // About to lock — collect an optional reason first
      setLockTarget(match);
      setLockReasonDraft("");
      setLockReasonVisibleDraft(false);
      return;
    }
    try {
      const updated = await setMatchLocked(match.id, false);
      setMatches(prev => prev.map(m => m.id === match.id ? updated : m));
      setSuccessMessage(t("admin_topic_unlocked"));
    } catch {
      setError(t("admin_action_failed"));
    }
  };

  const handleConfirmLock = async () => {
    if (!lockTarget) return;
    setLockBusy(true);
    try {
      const updated = await setMatchLocked(lockTarget.id, true, lockReasonDraft.trim() || undefined, lockReasonVisibleDraft);
      setMatches(prev => prev.map(m => m.id === lockTarget.id ? updated : m));
      setSuccessMessage(t("admin_topic_locked"));
      setLockTarget(null);
    } catch {
      setError(t("admin_action_failed"));
    } finally {
      setLockBusy(false);
    }
  };

  const handleCancelLock = () => {
    setLockTarget(null);
    setLockReasonDraft("");
    setLockReasonVisibleDraft(false);
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedMatch(null);
    setError(null);
    setSameDayWarning(null);
  };

  // Handle create match
  // force=true bypasses the same-day duplicate warning and proceeds with creation
  const handleCreateMatch = async (force: boolean = false) => {
    if (!formData.homeTeamId || !formData.awayTeamId || !formData.matchDate || !formData.matchTime) {
      setError(t("admin_match_fill_required"));
      return;
    }
    if (formData.homeTeamId === formData.awayTeamId) {
      setError(t("admin_match_same_team_error"));
      return;
    }
    try {
      await createAdminMatch(
        {
          homeTeamId: formData.homeTeamId,
          awayTeamId: formData.awayTeamId,
          // Convert admin-local date+time into UTC parts before persisting
          ...localInputToUtcParts(formData.matchDate, formData.matchTime),
          stadiumName: formData.stadiumName || undefined,
          sportType: formData.sportType,
          matchGender: formData.matchGender,
          league: formData.league || undefined,
          season: formData.season || undefined,
          round: formData.round || undefined,
          // Send score and status — homeScore/awayScore of 0 is valid, use explicit check
          homeScore: formData.homeScore !== undefined ? formData.homeScore : 0,
          awayScore: formData.awayScore !== undefined ? formData.awayScore : 0,
          status: formData.status,
          // ET and penalty scores — only sent when the sport supports them.
          // Normalized as pairs: one filled side defaults the other to 0.
          homeScoreEt: normalizePair(formData.homeScoreEt, formData.awayScoreEt)[0],
          awayScoreEt: normalizePair(formData.homeScoreEt, formData.awayScoreEt)[1],
          homeScorePen: normalizePair(formData.homeScorePen, formData.awayScorePen)[0],
          awayScorePen: normalizePair(formData.homeScorePen, formData.awayScorePen)[1],
          overtimePeriods: formData.overtimePeriods || 0,
        },
        force
      );
      setSameDayWarning(null);
      setSuccessMessage(t("admin_match_created"));
      closeModal();
      fetchMatches();
    } catch (err: any) {
      const message: string = err.message || "";

      // Same-day duplicate: show a confirmation warning, allow user to force-proceed
      if (message.includes("SAME_DAY_DUPLICATE")) {
        setSameDayWarning(t("admin_match_same_day_warning"));
        return;
      }

      // Exact duplicate: hard error, creation is blocked entirely
      if (message.includes("EXACT_DUPLICATE")) {
        setError(t("admin_match_exact_duplicate_error"));
        return;
      }

      setError(message || t("admin_match_create_error"));
    }
  };

  // ET/PEN pair normalization: these scores are "played or not" as a PAIR.
  // If the admin fills only one side (e.g. away 1, home left as "-"), the match
  // would be half-flagged and the UZ/PEN badge never renders. Treat the empty
  // side as 0 whenever the other side has a value; keep null/null as "not played".
  const normalizePair = (a: number | null, b: number | null): [number | null, number | null] => {
    if (a == null && b == null) return [null, null];
    return [a ?? 0, b ?? 0];
  };

  // Handle update match
  const handleUpdateMatch = async () => {
    if (!selectedMatch) return;
    try {
      await updateAdminMatch(selectedMatch.id, {
        // Send undefined (omitted from JSON) if 0 — backend keeps existing team
        homeTeamId: formData.homeTeamId || undefined,
        awayTeamId: formData.awayTeamId || undefined,
        // Convert admin-local date+time into UTC parts before persisting
        ...localInputToUtcParts(formData.matchDate, formData.matchTime),
        stadiumName: formData.stadiumName,
        sportType: formData.sportType,
        matchGender: formData.matchGender,
        league: formData.league,
        season: formData.season,
        round: formData.round,
        status: formData.status,
        homeScore: formData.homeScore,
        awayScore: formData.awayScore,
        // ET and penalty scores — only sent when ET/PEN section is visible for this sport.
        // Normalized as pairs: one filled side defaults the other to 0.
        homeScoreEt: normalizePair(formData.homeScoreEt, formData.awayScoreEt)[0],
        awayScoreEt: normalizePair(formData.homeScoreEt, formData.awayScoreEt)[1],
        homeScorePen: normalizePair(formData.homeScorePen, formData.awayScorePen)[0],
        awayScorePen: normalizePair(formData.homeScorePen, formData.awayScorePen)[1],
        overtimePeriods: formData.overtimePeriods,
        active: formData.active,
      });
      setSuccessMessage(t("admin_match_updated"));
      closeModal();
      fetchMatches();
    } catch (err: any) {
      setError(err.message || t("admin_match_update_error"));
    }
  };

  // Handle update score
  const handleUpdateScore = async () => {
    if (!selectedMatch) return;
    try {
      await updateMatchScore(selectedMatch.id, {
        homeScore: scoreForm.homeScore,
        awayScore: scoreForm.awayScore,
        // Normalized as pairs: one filled side defaults the other to 0.
        homeScoreEt: normalizePair(scoreForm.homeScoreEt, scoreForm.awayScoreEt)[0],
        awayScoreEt: normalizePair(scoreForm.homeScoreEt, scoreForm.awayScoreEt)[1],
        homeScorePen: normalizePair(scoreForm.homeScorePen, scoreForm.awayScorePen)[0],
        awayScorePen: normalizePair(scoreForm.homeScorePen, scoreForm.awayScorePen)[1],
        overtimePeriods: scoreForm.overtimePeriods,
      });
      setSuccessMessage(t("admin_match_score_updated"));
      closeModal();
      fetchMatches();
    } catch (err: any) {
      setError(err.message || t("admin_match_score_update_error"));
    }
  };

  // Handle update status
  const handleUpdateStatus = async () => {
    if (!selectedMatch) return;
    try {
      await updateMatchStatus(selectedMatch.id, { status: statusForm });
      setSuccessMessage(t("admin_match_status_updated"));
      closeModal();
      fetchMatches();
    } catch (err: any) {
      setError(err.message || t("admin_match_status_update_error"));
    }
  };

  // Handle delete match
  const handleDeleteMatch = async () => {
    if (!selectedMatch) return;
    try {
      await deleteAdminMatch(selectedMatch.id);
      setSuccessMessage(t("admin_match_deleted"));
      closeModal();
      fetchMatches();
    } catch (err: any) {
      setError(err.message || t("admin_match_delete_error"));
    }
  };

  // Helper: find teams for a given club filtered by current sport+gender
  const getTeamOptionsForClub = React.useCallback(
    (clubId: number) =>
      teams.filter(
        (t) =>
          Number(t.clubId) === Number(clubId) &&
          (!formData.sportType || t.sportType === formData.sportType) &&
          (!formData.matchGender || t.gender === formData.matchGender)
      ),
    [teams, formData.sportType, formData.matchGender]
  );

  // Step 1: user picks a club — compute team options immediately (no useEffect delay)
  const selectHomeClub = (club: ClubModel) => {
    const options = getTeamOptionsForClub(club.id);
    setSelectedHomeClub(club);
    setHomeClubSearch(club.shortName);
    setHomeTeamOptions(options);
    setShowHomeClubDropdown(false);
    // Auto-open the branch list right under the same input when several branches exist
    setShowHomeTeamBranchDropdown(options.length > 1);
    setFormData((p) => ({
      ...p,
      homeTeamId: options.length === 1 ? Number(options[0].id) : 0,
    }));
  };

  const selectAwayClub = (club: ClubModel) => {
    const options = getTeamOptionsForClub(club.id);
    setSelectedAwayClub(club);
    setAwayClubSearch(club.shortName);
    setAwayTeamOptions(options);
    setShowAwayClubDropdown(false);
    // Auto-open the branch list right under the same input when several branches exist
    setShowAwayTeamBranchDropdown(options.length > 1);
    setFormData((p) => ({
      ...p,
      awayTeamId: options.length === 1 ? Number(options[0].id) : 0,
    }));
  };

  // Clear club selection so user can search again
  const clearHomeClub = () => {
    setSelectedHomeClub(null);
    setHomeClubSearch("");
    setHomeTeamOptions([]);
    setShowHomeTeamBranchDropdown(false);
    setFormData((p) => ({ ...p, homeTeamId: 0 }));
  };

  const clearAwayClub = () => {
    setSelectedAwayClub(null);
    setAwayClubSearch("");
    setAwayTeamOptions([]);
    setShowAwayTeamBranchDropdown(false);
    setFormData((p) => ({ ...p, awayTeamId: 0 }));
  };

  // Step 2: user picks a specific branch (e.g. "1. Takım" vs "U19") from the custom dropdown
  // Number(...) coercion matches the same defensive pattern already used for clubId
  // elsewhere in this file — the API can return ids as either number or numeric string
  const selectHomeTeamBranch = (team: TeamModel) => {
    setFormData((p) => ({ ...p, homeTeamId: Number(team.id) }));
    setShowHomeTeamBranchDropdown(false);
  };

  const selectAwayTeamBranch = (team: TeamModel) => {
    setFormData((p) => ({ ...p, awayTeamId: Number(team.id) }));
    setShowAwayTeamBranchDropdown(false);
  };

  return (
    <div className="admin-matches">
      <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_match_search_placeholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
          </button>
        </div>

        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {t("admin_filters")}
          <FaChevronDown className={`chevron ${showFilters ? "open" : ""}`} />
        </button>

        {(filters.status || filters.sportType || filters.matchGender || filters.country || filters.active || filters.locked || filters.search || filters.startDate || filters.endDate) && (
          <button className="clear-filters" onClick={handleClearFilters}>
            <FaTimes /> {t("admin_clear_filters")}
          </button>
        )}

        <button className="create-match-btn" onClick={openCreateModal}>
          <FaPlus /> {t("admin_match_create")}
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_match_filter_status")}</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  status: e.target.value as MatchStatusEnum | "",
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_match_all_statuses")}</option>
              {Object.values(MatchStatusEnum).map((status) => (
                <option key={status} value={status}>
                  {t(`match_status_${status.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_match_filter_sport")}</label>
            <select
              value={filters.sportType}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  sportType: e.target.value as SportTypeEnum | "",
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_match_all_sports")}</option>
              {Object.values(SportTypeEnum).map((sport) => (
                <option key={sport} value={sport}>
                  {t(`sport_type_${sport.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_match_filter_gender")}</label>
            <select
              value={filters.matchGender}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  matchGender: e.target.value as MatchGenderEnum | "",
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_match_all_genders")}</option>
              {Object.values(MatchGenderEnum).map((gender) => (
                <option key={gender} value={gender}>
                  {t(`match_gender_${gender.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_country")}</label>
            <select
              value={filters.country || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, country: e.target.value }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all_countries")}</option>
              {/* Cross-country matches — not a real country code */}
              <option value={INTERNATIONAL_COUNTRY}>{t("pane_filter_international")}</option>
              {filterCountries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_match_visibility")}</label>
            <select
              value={filters.active}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  active: e.target.value as AdminMatchFilters["active"],
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_active")}</option>
              <option value="false">{t("admin_inactive")}</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_filter_date_from")}</label>
            <input
              type="date"
              value={filters.startDate || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, startDate: e.target.value || undefined }));
                setCurrentPage(0);
              }}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_filter_date_to")}</label>
            <input
              type="date"
              value={filters.endDate || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, endDate: e.target.value || undefined }));
                setCurrentPage(0);
              }}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_ct_lock_status")}</label>
            <select
              value={filters.locked}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  locked: e.target.value as AdminMatchFilters["locked"],
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_ct_locked")}</option>
              <option value="false">{t("admin_ct_unlocked")}</option>
            </select>
          </div>

          {/* Quick date shortcuts — sit right after the lock status filter */}
          <div className="admin-filter-group quick-date-group">
            <label>{t("admin_match_quick_date")}</label>
            <div className="quick-date-buttons">
              <button
                type="button"
                className={`quick-date-btn ${isQuickDateActive(-1) ? "active" : ""}`}
                onClick={() => applyQuickDate(-1)}
              >
                {t("menu_date_yesterday")}
              </button>
              <button
                type="button"
                className={`quick-date-btn ${isQuickDateActive(0) ? "active" : ""}`}
                onClick={() => applyQuickDate(0)}
              >
                {t("menu_date_today")}
              </button>
              <button
                type="button"
                className={`quick-date-btn ${isQuickDateActive(1) ? "active" : ""}`}
                onClick={() => applyQuickDate(1)}
              >
                {t("menu_date_tomorrow")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="matches-table-container">
        {loading ? (
          <div className="matches-loading">{t("loading")}</div>
        ) : error && matches.length === 0 ? (
          <div className="matches-error">{error}</div>
        ) : matches.length === 0 ? (
          <div className="matches-empty">{t("admin_match_no_matches")}</div>
        ) : (
          <table className="matches-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")}>
                  ID {getSortIcon("id")}
                </th>
                <th>{t("admin_match_col_teams")}</th>
                <th className="match-score">{t("admin_match_col_score")}</th>
                <th onClick={() => handleSort("matchDate")}>
                  {t("admin_match_col_date")} {getSortIcon("matchDate")}
                </th>
                <th className="match-league" onClick={() => handleSort("league")}>
                  {t("admin_match_col_league")} {getSortIcon("league")}
                </th>
                <th onClick={() => handleSort("status")}>
                  {t("admin_match_col_status")} {getSortIcon("status")}
                </th>
                <th className="match-entries" onClick={() => handleSort("entryCount")}>{t("admin_col_entries")} {getSortIcon("entryCount")}</th>
                <th className="match-referees-col">{t("admin_col_referees")}</th>
                <th className="match-active-col" onClick={() => handleSort("active")}>
                  {t("admin_match_visibility")} {getSortIcon("active")}
                </th>
                <th>{t("admin_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((match) => (
                <tr key={match.id} className={!match.active ? "row-inactive" : ""}>
                  <td className="match-id">#{match.id}</td>
                  <td className="match-teams">
                    <div className="team-vs team-link" onClick={() => viewMatch(match)}>
                      <span className="team-name" title={match.homeTeam.clubLongName || ""}>
                        {match.homeTeam.snapshotName}
                      </span>
                      <span className="vs-separator">vs</span>
                      <span className="team-name" title={match.awayTeam.clubLongName || ""}>
                        {match.awayTeam.snapshotName}
                      </span>
                    </div>
                    {match.stadiumName && (
                      <div className="match-stadium">{match.stadiumName}</div>
                    )}
                  </td>
                  <td className="match-score">
                    <span className={`score-display${
                      match.status === MatchStatusEnum.ABANDONED ? " score-display--abandoned"
                      : match.status === MatchStatusEnum.CANCELLED ? " score-display--cancelled"
                      : match.status === MatchStatusEnum.POSTPONED ? " score-display--postponed"
                      : ""
                    }`}>
                      {/* CANCELLED → İPT, POSTPONED → ERT, SCHEDULED → - : -, others show total score */}
                      {match.status === MatchStatusEnum.CANCELLED
                        ? "İPT"
                        : match.status === MatchStatusEnum.POSTPONED
                        ? "ERT"
                        : match.status === MatchStatusEnum.SCHEDULED
                        ? "- : -"
                        : `${match.homeScore + (match.homeScoreEt ?? 0) + (match.homeScorePen ?? 0)} - ${match.awayScore + (match.awayScoreEt ?? 0) + (match.awayScorePen ?? 0)}`}
                    </span>
                    {/* Show UZ or PEN badge below score when applicable */}
                    {match.homeScorePen != null && (
                      <span className="score-display__extra-badge score-display__extra-badge--pen">PEN</span>
                    )}
                    {match.homeScorePen == null && match.homeScoreEt != null && (
                      <span className="score-display__extra-badge score-display__extra-badge--et">UZT</span>
                    )}
                  </td>
                  <td className="match-date">
                    <div>{formatMatchDateLocal(match.matchDate, match.matchTime, "tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })}</div>
                    <div className="match-time">{formatMatchTimeLocal(match.matchDate, match.matchTime)}</div>
                  </td>
                  <td className="match-league">
                    <div>{match.league || "-"}</div>
                    {match.round && <div className="match-round">{match.round}</div>}
                  </td>
                  <td>
                    <span className={`match-status-badge ${getStatusColor(match.status)}`}>
                      {t(`match_status_${match.status.toLowerCase()}`)}
                    </span>
                  </td>
                  <td className="match-entries">{match.entryCount}</td>
                  <td className="match-referees-col">
                    {match.referees && match.referees.length > 0
                      ? <span className="match-referee-count">{match.referees.length}</span>
                      : <span className="text-muted-cell">—</span>
                    }
                  </td>
                  <td className="match-active-col">
                    {/* Quick-toggle public visibility directly from row */}
                    <button
                      className={`active-toggle-btn ${match.active ? "is-active" : "is-inactive"}`}
                      title={
                        match.active
                          ? t("admin_toggle_active_title")
                          : t("admin_toggle_inactive_title")
                      }
                      onClick={() => handleToggleActive(match)}
                    >
                      {match.active ? (
                        <>
                          <FaToggleOn /> {t("admin_active")}
                        </>
                      ) : (
                        <>
                          <FaToggleOff /> {t("admin_inactive")}
                        </>
                      )}
                    </button>
                  </td>
                  <td className="match-actions">
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        title={t("admin_match_action_edit")}
                        onClick={() => openEditModal(match)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="action-btn warning"
                        title={t("admin_match_action_score")}
                        onClick={() => openScoreModal(match)}
                      >
                        <FaHashtag />
                      </button>
                      <button
                        className="action-btn key"
                        title={t("admin_match_action_status")}
                        onClick={() => openStatusModal(match)}
                      >
                        <FaExchangeAlt />
                      </button>
                      <button
                        className="action-btn referee"
                        title={t("admin_match_action_referees")}
                        onClick={() => openRefereesModal(match)}
                      >
                        <FaUserTie />
                      </button>
                      <button
                        className={match.isLocked ? "action-btn lock-active" : "action-btn lock"}
                        title={match.isLocked ? t("admin_topic_unlock") : t("admin_topic_lock")}
                        onClick={() => handleToggleLock(match)}
                      >
                        {match.isLocked ? <FaUnlock size={12} /> : <FaLock size={12} />}
                      </button>
                      {match.isLocked && (
                        <button
                          className="action-btn"
                          title={t("admin_lock_info_title")}
                          onClick={() => setLockInfoTarget(match)}
                        >
                          <FaInfoCircle size={12} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          className="action-btn danger"
                          title={t("admin_action_delete")}
                          onClick={() => openDeleteModal(match)}
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="matches-pagination">
          <span className="page-info">
            {t("admin_match_page_info", {
              current: currentPage + 1,
              total: totalPages,
              items: totalItems,
            })}
          </span>
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            paginate={handlePaginate}
          />
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Create / Edit Modal */}
      {(modalType === "create" || modalType === "edit") && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal match-form-modal">
            <div className="admin-modal-header">
              <h3>
                {modalType === "create"
                  ? t("admin_match_modal_create_title")
                  : t("admin_match_modal_edit_title")}
              </h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="modal-error">{error}</div>}

              <div className="form-row">
                {/* Home team - Step 1: club search, Step 2: team select */}
                <div className="form-group team-select-group">
                  <label>{t("admin_match_home_team")} *</label>
                  <div className="team-search-wrapper">
                    {/* Club search input */}
                    <div className="club-search-row">
                      <div className="club-search-input-wrap" ref={homeBranchRef}>
                        <input
                          type="text"
                          placeholder={t("admin_match_search_club")}
                          value={homeClubSearch}
                          maxLength={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH}
                          onChange={(e) => {
                            setHomeClubSearch(e.target.value);
                            setSelectedHomeClub(null);
                            setShowHomeClubDropdown(true);
                            // Typing again means re-searching clubs — hide the branch list
                            setShowHomeTeamBranchDropdown(false);
                          }}
                          onFocus={() => setShowHomeClubDropdown(true)}
                          onClick={() => {
                            // Clicking the input with a selected multi-branch club re-opens the branch list
                            if (selectedHomeClub && homeTeamOptions.length > 1) setShowHomeTeamBranchDropdown(true);
                          }}
                          onBlur={() => setTimeout(() => setShowHomeClubDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                          className={selectedHomeClub ? "club-selected" : ""}
                        />
                        {/* Club dropdown results — lives inside the input-only wrapper so its
                            width always matches the input, never stretching into the clear button's column */}
                        {showHomeClubDropdown && filteredHomeClubs.length > 0 && (
                          <div className="team-dropdown">
                            {filteredHomeClubs.map((club) => (
                              <div
                                key={club.id}
                                className="team-dropdown-item"
                                onClick={() => selectHomeClub(club)}
                              >
                                <span className="team-dropdown-name">{club.shortName}</span>
                                {/* Use getCountryLabel to resolve ENG/SCO/WAL/NIR instead of raw GB */}
                                <span className="team-dropdown-country">{getCountryLabel(club.country)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Branch dropdown — anchored to the SAME input as the club results
                            above, so it opens flush under the field with the exact same look
                            (no detached trigger button). Never visible at the same time as
                            the club dropdown, since it requires selectedHomeClub. */}
                        {selectedHomeClub && homeTeamOptions.length > 1 && showHomeTeamBranchDropdown && (
                          <div className="team-dropdown team-branch-dropdown">
                            {homeTeamOptions.map((team) => (
                              <div
                                key={team.id}
                                className={`team-dropdown-item${Number(formData.homeTeamId) === Number(team.id) ? " team-dropdown-item-active" : ""}`}
                                title={getTeamDisplayName(team)}
                                onMouseDown={(e) => { e.preventDefault(); selectHomeTeamBranch(team); }}
                              >
                                <span className="team-dropdown-name">{getBranchShortLabel(team)}</span>
                                <span className="team-branch-badges">
                                  <span title={getBranchSportLabel(team.sportType)}>{getBranchSportIcon(team.sportType)}</span>
                                  <span title={getBranchGenderLabel(team.gender)}>{getBranchGenderIcon(team.gender)}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Clear button overlaid INSIDE the input's right edge — the input
                            itself now spans the full column width, so it lines up exactly
                            with the pill / prompt / dropdown rendered below it */}
                        {selectedHomeClub && (
                          <button type="button" className="club-clear-btn" onClick={clearHomeClub} title="Değiştir">
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                    <AdminCharCounter value={homeClubSearch} max={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH} />
                    {/* Step 2: show auto-selected team name (1 match) or select dropdown (>1 match) */}
                    {selectedHomeClub && homeTeamOptions.length === 1 && (
                      <div className="team-auto-selected">
                        ✓ {getTeamDisplayName(homeTeamOptions[0])}
                        <span className="team-auto-badge">
                          <span title={getBranchSportLabel(homeTeamOptions[0].sportType)}>{getBranchSportIcon(homeTeamOptions[0].sportType)}</span>
                          <span title={getBranchGenderLabel(homeTeamOptions[0].gender)}>{getBranchGenderIcon(homeTeamOptions[0].gender)}</span>
                        </span>
                      </div>
                    )}
                    {/* Multi-branch: selected branch pill (clickable to re-open the list)
                        or a muted prompt row while no branch has been chosen yet */}
                    {selectedHomeClub && homeTeamOptions.length > 1 && (
                      formData.homeTeamId ? (
                        (() => {
                          const sel = homeTeamOptions.find((team) => Number(team.id) === Number(formData.homeTeamId)) ?? homeTeamOptions[0];
                          return (
                            <div
                              className="team-auto-selected team-branch-selected"
                              onClick={() => setShowHomeTeamBranchDropdown(true)}
                              title={getTeamDisplayName(sel)}
                            >
                              ✓ {getBranchShortLabel(sel)}
                              <span className="team-auto-badge">
                                <span title={getBranchSportLabel(sel.sportType)}>{getBranchSportIcon(sel.sportType)}</span>
                                <span title={getBranchGenderLabel(sel.gender)}>{getBranchGenderIcon(sel.gender)}</span>
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <div
                          className="team-branch-prompt"
                          onClick={() => setShowHomeTeamBranchDropdown(true)}
                        >
                          {t("admin_match_select_team_branch")}
                          <FaChevronDown className="team-branch-prompt-chevron" />
                        </div>
                      )
                    )}
                    {selectedHomeClub && homeTeamOptions.length === 0 && formData.homeTeamId === 0 && (
                      <p className="team-no-branch">{t("admin_match_no_team_branch")}</p>
                    )}
                  </div>
                </div>

                {/* Away team - Step 1: club search, Step 2: team select */}
                <div className="form-group team-select-group">
                  <label>{t("admin_match_away_team")} *</label>
                  <div className="team-search-wrapper">
                    <div className="club-search-row">
                      <div className="club-search-input-wrap" ref={awayBranchRef}>
                        <input
                          type="text"
                          placeholder={t("admin_match_search_club")}
                          value={awayClubSearch}
                          maxLength={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH}
                          onChange={(e) => {
                            setAwayClubSearch(e.target.value);
                            setSelectedAwayClub(null);
                            setShowAwayClubDropdown(true);
                            // Typing again means re-searching clubs — hide the branch list
                            setShowAwayTeamBranchDropdown(false);
                          }}
                          onFocus={() => setShowAwayClubDropdown(true)}
                          onClick={() => {
                            // Clicking the input with a selected multi-branch club re-opens the branch list
                            if (selectedAwayClub && awayTeamOptions.length > 1) setShowAwayTeamBranchDropdown(true);
                          }}
                          onBlur={() => setTimeout(() => setShowAwayClubDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                          className={selectedAwayClub ? "club-selected" : ""}
                        />
                        {showAwayClubDropdown && filteredAwayClubs.length > 0 && (
                          <div className="team-dropdown">
                            {filteredAwayClubs.map((club) => (
                              <div
                                key={club.id}
                                className="team-dropdown-item"
                                onClick={() => selectAwayClub(club)}
                              >
                                <span className="team-dropdown-name">{club.shortName}</span>
                                {/* Use getCountryLabel to resolve ENG/SCO/WAL/NIR instead of raw GB */}
                                <span className="team-dropdown-country">{getCountryLabel(club.country)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Branch dropdown — anchored to the SAME input as the club results
                            above, so it opens flush under the field with the exact same look
                            (no detached trigger button). Never visible at the same time as
                            the club dropdown, since it requires selectedAwayClub. */}
                        {selectedAwayClub && awayTeamOptions.length > 1 && showAwayTeamBranchDropdown && (
                          <div className="team-dropdown team-branch-dropdown">
                            {awayTeamOptions.map((team) => (
                              <div
                                key={team.id}
                                className={`team-dropdown-item${Number(formData.awayTeamId) === Number(team.id) ? " team-dropdown-item-active" : ""}`}
                                title={getTeamDisplayName(team)}
                                onMouseDown={(e) => { e.preventDefault(); selectAwayTeamBranch(team); }}
                              >
                                <span className="team-dropdown-name">{getBranchShortLabel(team)}</span>
                                <span className="team-branch-badges">
                                  <span title={getBranchSportLabel(team.sportType)}>{getBranchSportIcon(team.sportType)}</span>
                                  <span title={getBranchGenderLabel(team.gender)}>{getBranchGenderIcon(team.gender)}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Clear button overlaid INSIDE the input's right edge — the input
                            itself now spans the full column width, so it lines up exactly
                            with the pill / prompt / dropdown rendered below it */}
                        {selectedAwayClub && (
                          <button type="button" className="club-clear-btn" onClick={clearAwayClub} title="Değiştir">
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                    <AdminCharCounter value={awayClubSearch} max={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH} />
                    {selectedAwayClub && awayTeamOptions.length === 1 && (
                      <div className="team-auto-selected">
                        ✓ {getTeamDisplayName(awayTeamOptions[0])}
                        <span className="team-auto-badge">
                          <span title={getBranchSportLabel(awayTeamOptions[0].sportType)}>{getBranchSportIcon(awayTeamOptions[0].sportType)}</span>
                          <span title={getBranchGenderLabel(awayTeamOptions[0].gender)}>{getBranchGenderIcon(awayTeamOptions[0].gender)}</span>
                        </span>
                      </div>
                    )}
                    {/* Multi-branch: selected branch pill (clickable to re-open the list)
                        or a muted prompt row while no branch has been chosen yet */}
                    {selectedAwayClub && awayTeamOptions.length > 1 && (
                      formData.awayTeamId ? (
                        (() => {
                          const sel = awayTeamOptions.find((team) => Number(team.id) === Number(formData.awayTeamId)) ?? awayTeamOptions[0];
                          return (
                            <div
                              className="team-auto-selected team-branch-selected"
                              onClick={() => setShowAwayTeamBranchDropdown(true)}
                              title={getTeamDisplayName(sel)}
                            >
                              ✓ {getBranchShortLabel(sel)}
                              <span className="team-auto-badge">
                                <span title={getBranchSportLabel(sel.sportType)}>{getBranchSportIcon(sel.sportType)}</span>
                                <span title={getBranchGenderLabel(sel.gender)}>{getBranchGenderIcon(sel.gender)}</span>
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <div
                          className="team-branch-prompt"
                          onClick={() => setShowAwayTeamBranchDropdown(true)}
                        >
                          {t("admin_match_select_team_branch")}
                          <FaChevronDown className="team-branch-prompt-chevron" />
                        </div>
                      )
                    )}
                    {/* Warning only after teams are loaded */}
                    {selectedAwayClub && awayTeamOptions.length === 0 && formData.awayTeamId === 0 && (
                      <p className="team-no-branch">{t("admin_match_no_team_branch")}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_match_date")} *</label>
                  <input
                    type="date"
                    value={formData.matchDate}
                    onChange={(e) => setFormData((p) => ({ ...p, matchDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>{t("admin_match_time")} *</label>
                  {/* Two selects instead of native time picker — much faster to use */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <select
                      value={formData.matchTime ? formData.matchTime.split(":")[0] : ""}
                      onChange={(e) => {
                        const h = e.target.value;
                        const m = formData.matchTime ? formData.matchTime.split(":")[1] || "00" : "00";
                        setFormData((p) => ({ ...p, matchTime: h ? `${h}:${m}` : "" }));
                      }}
                      style={{ flex: 1 }}
                    >
                      <option value="">SS</option>
                      {/* Hours start from 14 so that typical kick-off times (19:00 etc.) are visible near the top without scrolling */}
                      {Array.from({ length: 24 }, (_, i) => String((i + 14) % 24).padStart(2, "0")).map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <select
                      value={formData.matchTime ? formData.matchTime.split(":")[1] || "00" : "00"}
                      onChange={(e) => {
                        const m = e.target.value;
                        const h = formData.matchTime ? formData.matchTime.split(":")[0] || "00" : "00";
                        setFormData((p) => ({ ...p, matchTime: `${h}:${m}` }));
                      }}
                      style={{ flex: 1 }}
                    >
                      {["00", "15", "30", "45", "05", "10", "20", "25", "35", "40", "50", "55"].map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_match_stadium")}</label>
                  {/* Reuses the same team-search-wrapper / club-search-row / team-dropdown
                      classes as the home/away club search above. The dropdown lives inside
                      club-search-input-wrap (input-only), separate from the clear button,
                      so it never stretches wider than the actual text field.
                      className toggles to "club-selected" once a suggestion has actually
                      been picked, mirroring the highlight the club fields already get. */}
                  <div className="team-search-wrapper">
                    <div className="club-search-row">
                      <div className="club-search-input-wrap">
                        <input
                          type="text"
                          value={stadiumSearch}
                          onChange={async (e) => {
                            const q = e.target.value;
                            setStadiumSearch(q);
                            setStadiumConfirmed(false);
                            setFormData((p) => ({ ...p, stadiumName: q }));
                            if (q.trim().length >= 2) {
                              try {
                                const results = await stadiumService.search(q.trim());
                                setStadiumOptions(results.slice(0, 8));
                                setShowStadiumDropdown(true);
                              } catch {
                                setStadiumOptions([]);
                              }
                            } else {
                              setStadiumOptions([]);
                              setShowStadiumDropdown(false);
                            }
                          }}
                          onFocus={() => { if (stadiumOptions.length > 0) setShowStadiumDropdown(true); }}
                          onBlur={() => setTimeout(() => setShowStadiumDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                          placeholder={t("admin_match_stadium_placeholder")}
                          maxLength={ADMIN_MATCH_STADIUM_NAME_MAX_LENGTH}
                          className={stadiumConfirmed ? "club-selected" : ""}
                        />
                        {showStadiumDropdown && stadiumOptions.length > 0 && (
                          <div className="team-dropdown">
                            {stadiumOptions.map((st) => (
                              <div
                                key={st.id}
                                className="team-dropdown-item"
                                title={`${st.name}${st.teamDisplayName ? ` — ${st.teamDisplayName}` : ""}${st.city ? ` · ${st.city}` : ""}`}
                                onMouseDown={(e) => {
                                  // Prevent the input from blurring on this click so the
                                  // selection always registers, even if the browser would
                                  // otherwise fire blur before this handler runs
                                  e.preventDefault();
                                  setStadiumSearch(st.name);
                                  setStadiumConfirmed(true);
                                  setFormData((p) => ({ ...p, stadiumName: st.name }));
                                  setShowStadiumDropdown(false);
                                }}
                              >
                                <span className="team-dropdown-name">{st.name}</span>
                                <span className="team-dropdown-country">
                                  {st.teamDisplayName}{st.city ? ` · ${st.city}` : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Clear button overlaid INSIDE the input's right edge — keeps the
                            stadium input the same full width as its dropdown */}
                        {stadiumSearch && (
                          <button
                            type="button"
                            className="club-clear-btn"
                            onClick={() => { setStadiumSearch(""); setStadiumOptions([]); setStadiumConfirmed(false); setFormData((p) => ({ ...p, stadiumName: "" })); }}
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    </div>
                    <AdminCharCounter value={stadiumSearch} max={ADMIN_MATCH_STADIUM_NAME_MAX_LENGTH} />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_match_sport_type")}</label>
                  <select
                    value={formData.sportType}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, sportType: e.target.value as SportTypeEnum }))
                    }
                  >
                    {Object.values(SportTypeEnum).map((sport) => (
                      <option key={sport} value={sport}>
                        {t(`sport_type_${sport.toLowerCase()}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_match_gender")}</label>
                  <select
                    value={formData.matchGender}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        matchGender: e.target.value as MatchGenderEnum,
                      }))
                    }
                  >
                    {Object.values(MatchGenderEnum).map((gender) => (
                      <option key={gender} value={gender}>
                        {t(`match_gender_${gender.toLowerCase()}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ position: "relative" }}>
                  <label>{t("admin_match_league")}</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      type="text"
                      value={leagueSearch}
                      onChange={(e) => {
                        setLeagueSearch(e.target.value);
                        setFormData((p) => ({ ...p, league: e.target.value }));
                        setShowLeagueDropdown(true);
                      }}
                      onFocus={() => setShowLeagueDropdown(true)}
                      onBlur={() => setTimeout(() => setShowLeagueDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                      placeholder={t("admin_match_league_placeholder")}
                      maxLength={ADMIN_MATCH_LEAGUE_MAX_LENGTH}
                      style={{ flex: 1 }}
                    />
                    {leagueSearch && (
                      <button
                        type="button"
                        onClick={() => { setLeagueSearch(""); setFormData((p) => ({ ...p, league: "" })); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: "0 4px" }}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                  {showLeagueDropdown && (() => {
                    // Filter leagues from master data API
                    // Same Turkish-aware folding as the club picker above
                    const q = normalizeSearchText(leagueSearch.trim());
                    const filtered = q
                      ? mdLeagues.filter(l => normalizeSearchText(l.name).includes(q))
                      : mdLeagues;
                    return filtered.length > 0 ? (
                      <div className="club-dropdown">
                        {filtered.map((l) => (
                          <div
                            key={l.id}
                            className="club-dropdown-item"
                            onMouseDown={() => {
                              setLeagueSearch(l.name);
                              setFormData((p) => ({ ...p, league: l.name }));
                              setShowLeagueDropdown(false);
                            }}
                          >
                            <span className="club-dropdown-name">
                              {l.countryFlag ? `${l.countryFlag} ` : ""}{l.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  <AdminCharCounter value={leagueSearch} max={ADMIN_MATCH_LEAGUE_MAX_LENGTH} />
                </div>
                <div className="form-group">
                  <label>{t("admin_match_season")}</label>
                  <select
                    value={formData.season}
                    onChange={(e) => setFormData((p) => ({ ...p, season: e.target.value }))}
                  >
                    {/* Options run from 2000-2001 through one season beyond the current one — recomputed from today's date, no yearly code change needed */}
                    {Array.from({ length: Number(getCurrentSeason().slice(0, 4)) + 2 - 2000 }, (_, i) => `${2000 + i}-${2001 + i}`).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ position: "relative" }}>
                  <label>{t("admin_match_round")}</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      type="text"
                      value={roundSearch}
                      onChange={(e) => {
                        setRoundSearch(e.target.value);
                        setFormData((p) => ({ ...p, round: e.target.value }));
                        setShowRoundDropdown(true);
                      }}
                      onFocus={() => setShowRoundDropdown(true)}
                      onBlur={() => setTimeout(() => setShowRoundDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                      placeholder={t("admin_match_round_placeholder")}
                      maxLength={ADMIN_MATCH_ROUND_MAX_LENGTH}
                      style={{ flex: 1 }}
                    />
                    {roundSearch && (
                      <button
                        type="button"
                        onClick={() => { setRoundSearch(""); setFormData((p) => ({ ...p, round: "" })); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: "0 4px" }}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                  {showRoundDropdown && (() => {
                    // Filter rounds from master data API
                    // Same Turkish-aware folding as the club picker above
                    const q = normalizeSearchText(roundSearch.trim());
                    const filtered = q
                      ? mdRounds.filter(r => normalizeSearchText(r.name).includes(q))
                      : mdRounds;
                    return filtered.length > 0 ? (
                      <div className="club-dropdown">
                        {filtered.map((r) => (
                          <div
                            key={r.id}
                            className="club-dropdown-item"
                            onMouseDown={() => {
                              setRoundSearch(r.name);
                              setFormData((p) => ({ ...p, round: r.name }));
                              setShowRoundDropdown(false);
                            }}
                          >
                            <span className="club-dropdown-name">{r.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  <AdminCharCounter value={roundSearch} max={ADMIN_MATCH_ROUND_MAX_LENGTH} />
                </div>
              </div>

              {/* Show score and status fields for both create and edit modes */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_match_home_score")}</label>
                  {/* Use empty string only when value is null/undefined; 0 is a valid score and must be shown */}
                  <NumberStepper
                    value={formData.homeScore ?? null}
                    emptyValue={0}
                    onChange={(next) =>
                      setFormData((p) => ({ ...p, homeScore: next ?? 0 }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>{t("admin_match_away_score")}</label>
                  {/* Use empty string only when value is null/undefined; 0 is a valid score and must be shown */}
                  <NumberStepper
                    value={formData.awayScore ?? null}
                    emptyValue={0}
                    onChange={(next) =>
                      setFormData((p) => ({ ...p, awayScore: next ?? 0 }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>{t("admin_match_col_status")}</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        status: e.target.value as MatchStatusEnum,
                      }))
                    }
                  >
                    {Object.values(MatchStatusEnum).map((status) => (
                      <option key={status} value={status}>
                        {t(`match_status_${status.toLowerCase()}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Extra time scores — shown for sports that support ET */}
              {SPORTS_WITH_EXTRA_TIME.has(formData.sportType) && (
                <>
                  <div className="form-row-section-label">
                    {SPORTS_WITH_MULTI_OT.has(formData.sportType)
                      ? t("admin_match_score_ot")
                      : t("admin_match_score_et")}
                    <span className="form-label-optional"> ({t("admin_match_score_optional")})</span>
                  </div>
                  <div className="form-row">
                    {SPORTS_WITH_MULTI_OT.has(formData.sportType) && (
                      <div className="form-group" style={{ maxWidth: 120 }}>
                        <label>{t("admin_match_score_ot_periods")}</label>
                        <NumberStepper
                          value={formData.overtimePeriods ?? 0}
                          min={0}
                          max={20}
                          emptyValue={0}
                          onChange={(next) =>
                            setFormData((p) => ({ ...p, overtimePeriods: next ?? 0 }))
                          }
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label>{t("admin_match_home_score")}</label>
                      <NumberStepper
                        value={formData.homeScoreEt ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setFormData((p) => ({ ...p, homeScoreEt: next }))
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>{t("admin_match_away_score")}</label>
                      <NumberStepper
                        value={formData.awayScoreEt ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setFormData((p) => ({ ...p, awayScoreEt: next }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Penalty shootout scores — shown only for sports that support shootouts */}
              {SPORTS_WITH_PENALTY.has(formData.sportType) && (
                <>
                  <div className="form-row-section-label">
                    {t("admin_match_score_pen")}
                    <span className="form-label-optional"> ({t("admin_match_score_optional")})</span>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t("admin_match_home_score")}</label>
                      <NumberStepper
                        value={formData.homeScorePen ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setFormData((p) => ({ ...p, homeScorePen: next }))
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>{t("admin_match_away_score")}</label>
                      <NumberStepper
                        value={formData.awayScorePen ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setFormData((p) => ({ ...p, awayScorePen: next }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}
              {/* Active toggle — only on edit (same pattern as clubs/people/teams) */}
              {modalType === "edit" && (
                <div className="form-row">
                  <div className="form-group form-group--inline">
                    <label>{t("admin_active")}</label>
                    <button
                      type="button"
                      className={`active-toggle-btn ${formData.active ? "is-active" : "is-inactive"}`}
                      onClick={() =>
                        setFormData((p) => ({ ...p, active: !p.active }))
                      }
                    >
                      {formData.active ? (
                        <>
                          <FaToggleOn /> {t("admin_active")}
                        </>
                      ) : (
                        <>
                          <FaToggleOff /> {t("admin_inactive")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Same-day duplicate warning banner — shown inside the form body
                  so the footer buttons stay clean and uncluttered */}
              {sameDayWarning && modalType === "create" && (
                <div className="admin-match-same-day-warning">
                  <span className="warning-icon">⚠️</span>
                  <span className="warning-text">{sameDayWarning}</span>
                  <button
                    className="warning-dismiss-btn"
                    onClick={() => setSameDayWarning(null)}
                    title={t("close")}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              {/* When a same-day warning is active, replace the normal save button
                  with a highlighted "Create Anyway" button */}
              {sameDayWarning && modalType === "create" ? (
                <button
                  className="modal-confirm-btn match-force-create-btn"
                  onClick={() => handleCreateMatch(true)}
                >
                  {t("admin_match_same_day_confirm")}
                </button>
              ) : (
                <button
                  className="modal-confirm-btn"
                  onClick={modalType === "create" ? () => handleCreateMatch() : handleUpdateMatch}
                >
                  {modalType === "create" ? t("admin_match_create") : t("admin_match_save")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Score Modal */}
      {modalType === "score" && selectedMatch && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal score-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_match_modal_score_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="modal-error">{error}</div>}
              <div className="score-match-info">
                <span>{selectedMatch.homeTeam.snapshotName}</span>
                <span className="vs-separator">vs</span>
                <span>{selectedMatch.awayTeam.snapshotName}</span>
              </div>

              {/* Regular time scores — always shown */}
              <p className="score-section-label">{t("admin_match_score_regular")}</p>
              <div className="score-inputs">
                <div className="form-group">
                  <label>{selectedMatch.homeTeam.snapshotName}</label>
                  {/* Use empty string only when value is null/undefined; 0 is a valid score and must be shown */}
                  <NumberStepper
                    value={scoreForm.homeScore ?? null}
                    emptyValue={0}
                    onChange={(next) =>
                      setScoreForm((p) => ({ ...p, homeScore: next ?? 0 }))
                    }
                  />
                </div>
                <span className="score-separator">-</span>
                <div className="form-group">
                  <label>{selectedMatch.awayTeam.snapshotName}</label>
                  {/* Use empty string only when value is null/undefined; 0 is a valid score and must be shown */}
                  <NumberStepper
                    value={scoreForm.awayScore ?? null}
                    emptyValue={0}
                    onChange={(next) =>
                      setScoreForm((p) => ({ ...p, awayScore: next ?? 0 }))
                    }
                  />
                </div>
              </div>

              {/* Extra time scores — shown only for sports that support ET */}
              {SPORTS_WITH_EXTRA_TIME.has(selectedMatch.sportType) && (
                <>
                  <p className="score-section-label">
                    {SPORTS_WITH_MULTI_OT.has(selectedMatch.sportType)
                      ? t("admin_match_score_ot")
                      : t("admin_match_score_et")}
                    <span className="score-section-optional"> ({t("admin_match_score_optional")})</span>
                  </p>

                  {/* Multi-OT sports (basketball, ice hockey): show period counter + ET scores */}
                  {SPORTS_WITH_MULTI_OT.has(selectedMatch.sportType) && (
                    <div className="score-inputs score-inputs--narrow">
                      <div className="form-group">
                        <label>{t("admin_match_score_ot_periods")}</label>
                        <NumberStepper
                          value={scoreForm.overtimePeriods ?? 0}
                          min={0}
                          max={20}
                          emptyValue={0}
                          onChange={(next) =>
                            setScoreForm((p) => ({ ...p, overtimePeriods: next ?? 0 }))
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="score-inputs">
                    <div className="form-group">
                      <label>{selectedMatch.homeTeam.snapshotName}</label>
                      <NumberStepper
                        value={scoreForm.homeScoreEt ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setScoreForm((p) => ({ ...p, homeScoreEt: next }))
                        }
                      />
                    </div>
                    <span className="score-separator">-</span>
                    <div className="form-group">
                      <label>{selectedMatch.awayTeam.snapshotName}</label>
                      <NumberStepper
                        value={scoreForm.awayScoreEt ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setScoreForm((p) => ({ ...p, awayScoreEt: next }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Penalty shootout scores — shown only for sports that support shootouts */}
              {SPORTS_WITH_PENALTY.has(selectedMatch.sportType) && (
                <>
                  <p className="score-section-label">
                    {t("admin_match_score_pen")}
                    <span className="score-section-optional"> ({t("admin_match_score_optional")})</span>
                  </p>
                  <div className="score-inputs">
                    <div className="form-group">
                      <label>{selectedMatch.homeTeam.snapshotName}</label>
                      <NumberStepper
                        value={scoreForm.homeScorePen ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setScoreForm((p) => ({ ...p, homeScorePen: next }))
                        }
                      />
                    </div>
                    <span className="score-separator">-</span>
                    <div className="form-group">
                      <label>{selectedMatch.awayTeam.snapshotName}</label>
                      <NumberStepper
                        value={scoreForm.awayScorePen ?? null}
                        placeholder="-"
                        emptyValue={null}
                        onChange={(next) =>
                          setScoreForm((p) => ({ ...p, awayScorePen: next }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button className="modal-confirm-btn" onClick={handleUpdateScore}>
                {t("admin_match_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {modalType === "status" && selectedMatch && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal status-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_match_modal_status_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="modal-error">{error}</div>}
              <div className="score-match-info">
                <span>{selectedMatch.homeTeam.snapshotName}</span>
                <span className="vs-separator">vs</span>
                <span>{selectedMatch.awayTeam.snapshotName}</span>
              </div>
              <div className="form-group">
                <label>{t("admin_match_new_status")}</label>
                <select
                  value={statusForm}
                  onChange={(e) => setStatusForm(e.target.value as MatchStatusEnum)}
                >
                  {Object.values(MatchStatusEnum).map((status) => (
                    <option key={status} value={status}>
                      {t(`match_status_${status.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button className="modal-confirm-btn" onClick={handleUpdateStatus}>
                {t("admin_match_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {modalType === "delete" && selectedMatch && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal delete-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_match_modal_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="modal-error">{error}</div>}
              <div className="delete-match-info">
                <FaFutbol size={32} />
                <p>
                  <strong>
                    {selectedMatch.homeTeam.snapshotName} vs {selectedMatch.awayTeam.snapshotName}
                  </strong>
                </p>
                <p>
                  {formatMatchDateLocal(selectedMatch.matchDate, selectedMatch.matchTime, "tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })} - {formatMatchTimeLocal(selectedMatch.matchDate, selectedMatch.matchTime)}
                </p>
                {selectedMatch.entryCount > 0 && (
                  <div className="delete-entry-warning">
                    ⚠️ {t("admin_match_delete_entry_warning", { count: selectedMatch.entryCount })}
                  </div>
                )}
                <p className="delete-warning-text">{t("admin_match_delete_warning")}</p>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button className="modal-delete-btn" onClick={handleDeleteMatch}>
                {t("admin_action_delete")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Referee Management Modal */}
      {modalType === "referees" && selectedMatch && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal referee-modal">
            <div className="admin-modal-header">
              <h3>
                {t("admin_match_referees_title")}
                <span className="referee-modal__match-label">
                  — {selectedMatch.homeTeam.snapshotName} vs {selectedMatch.awayTeam.snapshotName}
                </span>
              </h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="admin-modal-body">
              {refereeError && <div className="modal-error">{refereeError}</div>}

              {/* Current referee list */}
              <div className="referee-panel">
                <p className="referee-panel__title">
                  {t("admin_match_referees_current")} ({selectedMatch.referees?.length ?? 0})
                </p>
                {selectedMatch.referees && selectedMatch.referees.length > 0 ? (
                  <div className="referee-panel__list">
                    {selectedMatch.referees.map((ref) => (
                      <div key={ref.assignmentId} className="referee-panel__entry">
                        <div className="referee-panel__entry-info">
                          <span className="referee-panel__name">{ref.displayName}</span>
                          <span className="referee-panel__role">
                            {t(`match_referee_role_${ref.refereeRole.toLowerCase()}`)}
                          </span>
                        </div>
                        <button
                          className="action-btn danger"
                          title={t("admin_delete_btn")}
                          onClick={() => handleRemoveReferee(ref)}
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="referee-panel__empty">{t("admin_match_referees_empty")}</p>
                )}
              </div>

              {/* Add referee form */}
              <div className="referee-panel__add-form">
                <p className="referee-panel__title">{t("admin_match_referee_add")}</p>
                <div className="form-row">
                  {/* Person search — typeahead over admin people list */}
                  <div className="form-group form-group--full">
                    <label>{t("admin_match_referee_person")} *</label>
                    <div className="club-search-wrapper" ref={refereeDropRef}>
                      <input
                        type="text"
                        className={`club-search-input ${selectedReferee ? "club-input--selected" : ""}`}
                        placeholder={t("admin_match_referee_search_placeholder")}
                        value={refereeQuery}
                        maxLength={ADMIN_PERSON_NAME_MAX_LENGTH}
                        onChange={(e) => {
                          setRefereeQuery(e.target.value);
                          if (selectedReferee) setSelectedReferee(null);
                        }}
                        onFocus={() => refereeResults.length > 0 && setShowRefereeDrop(true)}
                      />
                      {refereeSearchLoading && <span className="club-search-spinner" />}
                      {selectedReferee && !refereeSearchLoading && (
                        <span className="club-input-check">✓</span>
                      )}
                      {showRefereeDrop && refereeResults.length > 0 && (
                        <ul className="club-search-dropdown">
                          {refereeResults.map((person) => (
                            <li
                              key={person.id}
                              onMouseDown={() => {
                                setSelectedReferee(person);
                                setRefereeQuery(person.knownName ?? person.fullName);
                                setRefereeResults([]);
                                setShowRefereeDrop(false);
                              }}
                            >
                              {person.knownName ?? person.fullName}
                              {person.nationality && (
                                <span className="club-search-country">{person.nationality}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <AdminCharCounter value={refereeQuery} max={ADMIN_PERSON_NAME_MAX_LENGTH} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t("admin_match_referee_role")} *</label>
                    <select
                      value={refereeForm.refereeRole}
                      onChange={(e) => setRefereeForm({ refereeRole: e.target.value as MatchRefereeRole })}
                    >
                      {getRefereeRolesForSport(selectedMatch.sportType).map((role) => (
                        <option key={role} value={role}>
                          {t(`match_referee_role_${role.toLowerCase()}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group referee-add-btn-group">
                    <label>&nbsp;</label>
                    <button
                      className="btn-primary"
                      onClick={handleAddReferee}
                      disabled={refereeSaving || !selectedReferee}
                    >
                      <FaPlus size={11} /> {t("admin_add")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock-with-reason modal — only shown when locking, unlock stays one-click */}
      {lockTarget && (
        <ConfirmModal
          message={t("admin_match_lock_confirm")}
          confirmLabel={lockBusy ? "..." : t("admin_topic_lock")}
          cancelLabel={t("admin_cancel")}
          onConfirm={handleConfirmLock}
          onCancel={handleCancelLock}
          disabled={lockBusy}
        >
          <LockReasonFields
            reason={lockReasonDraft}
            onReasonChange={setLockReasonDraft}
            visible={lockReasonVisibleDraft}
            onVisibleChange={setLockReasonVisibleDraft}
          />
        </ConfirmModal>
      )}

      {lockInfoTarget && (
        <LockInfoModal
          lockedByUsername={lockInfoTarget.lockedByUsername}
          lockedAt={lockInfoTarget.lockedAt}
          lockReason={lockInfoTarget.lockReason}
          lockReasonVisible={lockInfoTarget.lockReasonVisible}
          onClose={() => setLockInfoTarget(null)}
        />
      )}
    </div>
  );
};